import { observer } from "mobx-react-lite";
import { appState } from "@/app/appState";
import { organisation_tree_node } from "@/app/typing/organisations";
import { Add_new, ArrowDown, ArrowRight } from "@/components/UI/svgs";
import { canWrite, flattenOrgTree } from "./tree";
import { Pill, settingsRowId } from "./ui";

const INDENT_PER_LEVEL = 14;

export const OrgTree = observer(({
    nodes,
    loading,
    search,
    setSearch,
    expanded,
    toggleExpanded,
    selectedOrgId,
    markedOrgIds,
    onSelect,
    onCreateChild,
}: {
    nodes: organisation_tree_node[];
    loading: boolean;
    search: string;
    setSearch: (val: string) => void;
    expanded: Set<number>;
    toggleExpanded: (id: number) => void;
    selectedOrgId?: number;
    markedOrgIds?: Set<number>;
    onSelect: (id: number) => void;
    onCreateChild: (parentId: number) => void;
}) => {
    const rows = flattenOrgTree(nodes, { expanded, search });
    const activeOrgId = appState.user?.active_organisation?.id;

    return (
        <div className="flex flex-col gap-[.3rem] min-h-0">
            <div className="px-[.35rem] pb-[.35rem]">
                <input
                    className="search"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={appState.t("common.filterPlaceholder")}
                />
            </div>

            {loading && rows.length === 0 && <p className="tree-empty">{appState.t("common.loading")}</p>}
            {!loading && rows.length === 0 && (
                <p className="tree-empty">{appState.t("settings.org.noMatch")}</p>
            )}

            {rows.map(({ node, depth, hasChildren }) => {
                const isSelected = node.id === selectedOrgId;

                return (
                    <div
                        key={node.id}
                        id={settingsRowId("org", node.id)}
                        onClick={() => onSelect(node.id)}
                        style={{ paddingLeft: depth * INDENT_PER_LEVEL }}
                        className={`tree-node ${isSelected ? "tree-node-selected" : ""}`}
                    >
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                if (hasChildren) toggleExpanded(node.id);
                            }}
                            className={`tree-node-toggle ${hasChildren ? "" : "tree-node-toggle-empty"}`}
                        >
                            {expanded.has(node.id) ? <ArrowDown size={14} /> : <ArrowRight size={14} />}
                        </span>

                        <span className="tree-node-label" title={node.name}>
                            {node.name}
                        </span>

                        {markedOrgIds?.has(node.id) && <Pill tone="accent" title={appState.t("settings.org.pill.setTitle")}>{appState.t("settings.org.pill.set")}</Pill>}
                        {node.id === activeOrgId && <Pill tone="accent" title={appState.t("settings.org.pill.hereTitle")}>{appState.t("settings.org.pill.here")}</Pill>}
                        {node.has_payment && <Pill title={appState.t("settings.org.pill.paysTitle")}>{appState.t("settings.org.pill.pays")}</Pill>}
                        {node.demo_access && <Pill tone="warn" title={appState.t("settings.org.pill.demoTitle")}>{appState.t("settings.org.pill.demo")}</Pill>}

                        {canWrite(node) && (
                            <span
                                title={appState.t("settings.org.newUnder")}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCreateChild(node.id);
                                }}
                                className="tree-node-action"
                            >
                                <Add_new size={14} />
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
});
